import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import type { SignupProgramOptions } from '@galapagos/shared/registration';
import type { SignupApi } from '../services/signup-api/signup-api';
import { useSignupApi } from './useSignupApi';

/** The program options' cache key, for readers that only look in the cache. */
export function signupProgramOptionsQueryKey(scenario: string | null) {
  return ['signup', 'program-options', scenario] as const;
}

/**
 * The one query for the program options, shared by the hook and by steps
 * that need the options at a moment of their own, so both read one cache.
 * One structure serves every language; the words come from the catalog. No
 * retry: the content is served from the bundle, so a failure would not heal
 * on a second try.
 */
export function signupProgramOptionsQuery(api: SignupApi, scenario: string | null) {
  return queryOptions({
    queryKey: signupProgramOptionsQueryKey(scenario),
    queryFn: () => api.getProgramOptions(),
    retry: false,
  });
}

/** The program options the substance step offers, through the signup data seam. */
export function useSignupProgramOptions(): UseQueryResult<SignupProgramOptions> {
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  return useQuery(signupProgramOptionsQuery(api, scenario));
}
